import { type NavigateFunction } from 'react-router-dom'

import {
  type InfraImpactFormValues,
  type InfraTargetFormValues,
  type InfraTriggerFormValues
} from 'components/attacks/create/utils/infra-initial-form-state'
import { type Client } from 'types/api/attacks/client'
import { type InfraAttack } from 'types/api/attacks/details/infra-attack'
import {
  type AttackCommand,
  type AttackConfiguration
} from 'types/api/attacks/details/targeting'
import { type K8sClient, type K8sObject } from 'types/api/kubernetes/kubernetes'
import { type StepAttack } from 'types/api/scenarios/scenario'
import { type UserDefinedService } from 'types/api/services/service'
import { type Metadata } from 'types/metadata'

export type CreateAttackStage = 'selectTarget' | 'target' | 'impact' | 'trigger'

export enum TargetType {
  Hosts = 'Hosts',
  Containers = 'Containers',
  Kubernetes = 'Kubernetes',
  Application = 'Applications',
  Services = 'Services'
}

export enum ProvenanceTargetType {
  ProcessData = 'Host',
  Container = 'Container',
  Kubernetes = 'Kubernetes',
  // The following enums are to support legacy references to target type
  // TODO: These should look to be removed throughout the app
  PROCESSDATA = 'Host',
  CONTAINER = 'Container',
  KUBERNETES = 'Kubernetes'
}

export type AttackFormOnSubmit = (
  targetType: TargetType,
  params: {
    target: InfraTargetFormValues
    impact: InfraImpactFormValues
    trigger: InfraTriggerFormValues
  },
  metadata: Metadata,
  currentTeamId: string,
  navigate: NavigateFunction,
  meta: { k8sTargets?: K8sObject[] },
  attackConfig?: AttackConfiguration
) => boolean | void | Promise<boolean | void>

export interface AttackFormProps {
  activeStage: CreateAttackStage
  cancelButtonText?: string
  displayGremlinImpact?: boolean
  displayScheduling?: boolean
  displayTargeting?: boolean
  displayTargetingImpact?: boolean
  expandAllStages?: boolean
  forcedAttackCommand?: AttackCommand
  infraClients: Client[]
  infraMetadata: Metadata
  infraProviders: string[]
  isLoading: boolean
  isScenario?: boolean
  onCancel(): void
  onSubmit?: AttackFormOnSubmit
  service?: UserDefinedService
  submitButtonText?: string
  targetType: TargetType
  updateActiveStage(stage: CreateAttackStage): void
}

export type InfraTargetType = TargetType.Hosts | TargetType.Containers

export type LabelKey = {
  disabled?: Set<string>
  name: string
  targets: string[]
  values: Set<string>
}

export interface InfraAttackFormProps extends AttackFormProps {
  infraAttackParams: Partial<InfraAttack>
  targetType: TargetType
}

export interface KubernetesAttackFormProps extends AttackFormProps {
  attackParams: Partial<InfraAttack>
  isK8ClientsLoading: boolean
  k8sClientsRaw: K8sClient[]
  targetType: TargetType
}

export interface CreateServiceAttackProps
  extends Pick<
      AttackFormProps,
      | 'activeStage'
      | 'infraMetadata'
      | 'infraProviders'
      | 'isLoading'
      | 'isScenario'
      | 'submitButtonText'
      | 'updateActiveStage'
    >,
    Pick<KubernetesAttackFormProps, 'isK8ClientsLoading' | 'k8sClientsRaw'>,
    Partial<Pick<KubernetesAttackFormProps, 'attackParams'>> {
  lastStep?: StepAttack
  onScenarioServicesSubmit?: AttackFormOnSubmit
}

export enum AttackTargetHealthErrors {
  ERROR_OS = 'os',
  ERROR_HOST = 'host',
  ERROR_TARGET_HEALTH = 'target_health'
}

export type AttackTargetAvailabilityResponse = {
  unavailable: boolean
  partiallyAvailable?: boolean
  unsupportedOS?: string[]
  supportedOS?: string[]
  error?: string
}

export type AttackTargetAvailability = ((
  command: AttackCommand
) => AttackTargetAvailabilityResponse) & {
  ERROR_OS?: AttackTargetHealthErrors.ERROR_OS
  ERROR_HOST?: AttackTargetHealthErrors.ERROR_HOST
  ERROR_TARGET_HEALTH?: AttackTargetHealthErrors.ERROR_TARGET_HEALTH
}

export type AttackTargetHealth = ((command: AttackCommand) => boolean) & {
  ERROR_TARGET_HEALTH?: AttackTargetHealthErrors.ERROR_TARGET_HEALTH
}
